/**
 * Auth store: the signed-in account (if any).
 *
 * Tokens live in lib/authToken.ts (localStorage, read directly by apiFetch
 * to avoid an import cycle). This store keeps the user profile + session
 * lifecycle. `initialized` flips true once the app has attempted a
 * session restore on load.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  staffLogin as apiStaffLogin,
  type AuthUser,
} from '../lib/auth';
import { ApiError } from '../lib/api';
import { clearTokens, getAccessToken } from '../lib/authToken';

interface AuthState {
  user: AuthUser | null;
  initialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Sign in to this city's staff account (tenant-bound session). */
  staffLogin: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  restore: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      initialized: false,
      login: async (email: string, password: string) => {
        const pair = await apiLogin(email, password);
        set({ user: pair.user, initialized: true });
      },
      staffLogin: async (email: string, password: string) => {
        const pair = await apiStaffLogin(email, password);
        set({ user: pair.user, initialized: true });
      },
      logout: async () => {
        await apiLogout();
        set({ user: null });
      },
      restore: async () => {
        if (!getAccessToken()) {
          set({ user: null, initialized: true });
          return;
        }
        try {
          const user = await fetchMe();
          set({ user, initialized: true });
        } catch (error) {
          if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
            // The server rejected the session (expired, revoked, other city):
            // drop to the signed-out state.
            clearTokens();
            set({ user: null, initialized: true });
            return;
          }
          // Network blip or server error: keep the session and the cached
          // profile rather than signing the person out; the next request
          // re-checks it.
          set((state) => ({ user: state.user, initialized: true }));
        }
      },
      setUser: (user: AuthUser | null) => set({ user }),
    }),
    {
      name: 'rdp.auth',
      partialize: (state) => ({ user: state.user }),
    },
  ),
);

/** Selector: signed in with a usable session. */
export function selectIsAuthenticated(state: AuthState): boolean {
  return state.user !== null && getAccessToken() !== null;
}

/** Friendly name for the header ("Resident" / email prefix fallback). */
export function displayNameFor(user: AuthUser): string {
  if (user.display_name && user.display_name.trim().length > 0) return user.display_name;
  const prefix = user.email.split('@')[0] ?? '';
  return prefix.length > 0 ? prefix : user.email;
}
